
import React from 'react';

interface IzakayaPricingProps {
  lang: 'ja' | 'en';
}

export const IzakayaPricing: React.FC<IzakayaPricingProps> = ({ lang }) => {
  const content = {
    ja: {
      title: '料金プラン',
      subtitle: '飲み代込みで安心！わかりやすい料金設定',
      recommended: '🔥 インバウンド人気No.1',
      plans: [
        {
          name: '飲み代込みプラン',
          tag: 'おすすめ',
          price: '¥15,000〜¥25,000',
          unit: '/ 1名',
          highlight: true,
          features: [
            '2ドリンク＋軽食 or 3軒はしご',
            '店舗コース利用（飲食代込み）',
            'ガイド同行（3時間）',
            '最大4名まで同料金',
            '交通費込み',
          ],
          note: '追加1名 +¥5,000',
          cta: 'このプランで予約相談する',
        },
        {
          name: 'ガイドのみプラン',
          tag: 'シンプル',
          price: '¥15,000〜',
          unit: '/ グループ（最大4名）',
          highlight: false,
          features: [
            'ガイド同行（3時間）',
            '飲食代は各店舗で直接お支払い',
            '最大4名まで同料金',
            '交通費込み',
          ],
          note: '追加1名 +¥5,000',
          cta: 'このプランで予約相談する',
        },
      ],
      notes: [
        '料金はグループ人数・コース内容により変動します',
        '移動は徒歩または公共交通機関を利用します',
        '詳細はお気軽にWhatsAppでご相談ください 😊',
      ],
      cta: 'WhatsAppで相談する',
    },
    en: {
      title: 'Pricing Plans',
      subtitle: 'Drinks included — no surprises!',
      recommended: '🔥 Most Popular for Inbound Guests',
      plans: [
        {
          name: 'All-Inclusive Plan',
          tag: 'Recommended',
          price: '¥15,000〜¥25,000',
          unit: '/ person',
          highlight: true,
          features: [
            '2 drinks + snacks OR 3-bar hopping',
            'Course menu at each venue (drinks included)',
            'Guide (3 hours)',
            'Up to 4 people same price',
            'Transit costs included',
          ],
          note: '+¥5,000 per additional person',
          cta: 'Inquire About This Plan',
        },
        {
          name: 'Guide Only Plan',
          tag: 'Simple',
          price: '¥15,000〜',
          unit: '/ group (up to 4)',
          highlight: false,
          features: [
            'Guide (3 hours)',
            'Food & drinks paid at each venue',
            'Up to 4 people same price',
            'Transit costs included',
          ],
          note: '+¥5,000 per additional person',
          cta: 'Inquire About This Plan',
        },
      ],
      notes: [
        'Price varies by group size and course selection',
        'Transportation by foot or public transit',
        'Feel free to ask us anything via WhatsApp 😊',
      ],
      cta: 'Chat on WhatsApp',
    },
  };

  const t = content[lang];

  const handleWhatsAppClick = (planName: string) => {
    const message =
      lang === 'ja'
        ? `Tokyo Local Izakaya Nightの「${planName}」について相談したいです`
        : `Hi! I'm interested in the "${planName}" for Tokyo Local Izakaya Night — a local bar-hopping experience in Tokyo's hidden neighborhoods. Could you share availability and what's included?`;
    const whatsappUrl = `https://wa.me/819092895522?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-4">
          <span className="inline-block bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            {t.recommended}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">{t.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-12 mb-12">
          {t.plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative rounded-2xl p-8 flex flex-col ${
                plan.highlight
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-2xl scale-105'
                  : 'bg-white border-2 border-gray-200 text-gray-800 shadow-md'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow whitespace-nowrap">
                    {plan.tag}
                  </span>
                </div>
              )}
              {!plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gray-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow whitespace-nowrap">
                    {plan.tag}
                  </span>
                </div>
              )}

              <h3 className={`text-2xl font-bold mb-2 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                {plan.name}
              </h3>
              <div className="mb-1">
                <span className={`text-4xl font-extrabold ${plan.highlight ? 'text-white' : 'text-amber-600'}`}>
                  {plan.price}
                </span>
              </div>
              <p className={`text-sm mb-6 ${plan.highlight ? 'text-amber-100' : 'text-gray-500'}`}>
                {plan.unit}
              </p>

              <ul className="space-y-3 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className={`ri-check-line text-lg mt-0.5 ${plan.highlight ? 'text-amber-200' : 'text-amber-500'}`}></i>
                    <span className={`text-base ${plan.highlight ? 'text-white' : 'text-gray-700'}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <p className={`text-sm mb-6 ${plan.highlight ? 'text-amber-100' : 'text-gray-500'}`}>
                ※ {plan.note}
              </p>

              <button
                onClick={() => handleWhatsAppClick(plan.name)}
                className={`w-full flex items-center justify-center gap-2 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap ${
                  plan.highlight
                    ? 'bg-white text-amber-600 hover:bg-amber-50 shadow-lg'
                    : 'bg-green-500 hover:bg-green-600 text-white shadow'
                }`}
              >
                <i className="ri-whatsapp-line text-xl"></i>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl p-8 border border-amber-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-information-line text-2xl text-amber-500"></i>
            <h3 className="text-lg font-bold text-gray-900">
              {lang === 'ja' ? 'ご注意・補足' : 'Notes'}
            </h3>
          </div>
          <ul className="space-y-2">
            {t.notes.map((note, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-600 text-base">
                <i className="ri-arrow-right-s-line text-amber-400 mt-0.5"></i>
                {note}
              </li>
            ))}
          </ul>
        </div>

        {/* Post-Pricing CTA */}
        <div className="mt-12 rounded-3xl overflow-hidden shadow-2xl relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/9d7275800a455e7d75be9162c45446f5.jpg')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/60 to-black/40" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 px-10 py-12">
            <div className="text-white text-center md:text-left">
              <p className="text-amber-400 text-sm font-semibold uppercase tracking-widest mb-2">
                {lang === 'ja' ? '料金を確認したら、次は予約！' : 'Liked the price? Book now!'}
              </p>
              <h3 className="text-3xl md:text-4xl font-extrabold leading-tight mb-3">
                {lang === 'ja'
                  ? <>気になるプランが見つかりましたか？<br /><span className="text-amber-400">今すぐWhatsAppで相談</span>できます。</>
                  : <>Found a plan that works for you?<br /><span className="text-amber-400">Chat with us on WhatsApp</span> right now.</>}
              </h3>
              <p className="text-gray-300 text-base">
                {lang === 'ja'
                  ? '日程・人数・ご要望をお気軽にメッセージください。24時間以内にご返信します。'
                  : 'Send us your date, group size, and any questions. We reply within 24 hours.'}
              </p>
            </div>
            <div className="flex flex-col items-center gap-4 shrink-0">
              <button
                onClick={() => handleWhatsAppClick(lang === 'ja' ? '料金確認後の予約相談' : 'Pricing Inquiry')}
                className="flex items-center gap-3 bg-green-500 hover:bg-green-400 text-white font-bold text-lg px-10 py-5 rounded-full shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                {lang === 'ja' ? 'WhatsAppで予約相談する' : 'Book via WhatsApp'}
              </button>
              <p className="text-gray-400 text-sm">
                {lang === 'ja' ? '返信率100% ・ 無料相談' : '100% reply rate · Free consultation'}
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
